import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import DashboardLayout from '../layouts/DashboardLayout';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { qrApi } from '../lib/api';
import { extractInternalScanQrId, normalizeScanPayload } from '../lib/parseQrPayload';
import { getWebScanDeviceId } from '../lib/webScanDeviceId';
import { Scan } from '@phosphor-icons/react';
import { toast } from 'sonner';

function mayScan(user) {
  if (!user || user === false) return false;
  const role = String(user.role || '').toLowerCase();
  const perms = Array.isArray(user.permissions) ? user.permissions : [];
  return role === 'admin' || perms.includes('all') || perms.includes('qr.scan');
}

export default function FieldScanPage() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { theme } = useTheme();
  const isDark = theme === 'dark';
  const inputRef = useRef(null);
  const [line, setLine] = useState('');
  const [loading, setLoading] = useState(false);
  const [locReady, setLocReady] = useState(false);
  const [locError, setLocError] = useState('');
  const posRef = useRef({ latitude: null, longitude: null, accuracy_m: null });

  const allowed = mayScan(user);

  const refreshLocation = useCallback(() => {
    if (typeof navigator === 'undefined' || !navigator.geolocation) {
      setLocError(t('fieldScan.noGeolocation'));
      setLocReady(false);
      return;
    }
    setLocError('');
    navigator.geolocation.getCurrentPosition(
      (position) => {
        posRef.current = {
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
          accuracy_m: typeof position.coords.accuracy === 'number' ? position.coords.accuracy : null,
        };
        setLocReady(true);
        setLocError('');
      },
      () => {
        posRef.current = { latitude: null, longitude: null, accuracy_m: null };
        setLocReady(false);
        setLocError(t('fieldScan.locationDenied'));
      },
      { enableHighAccuracy: true, timeout: 12000, maximumAge: 60000 },
    );
  }, [t]);

  useEffect(() => {
    refreshLocation();
  }, [refreshLocation]);

  useEffect(() => {
    const tmr = setTimeout(() => inputRef.current?.focus(), 100);
    return () => clearTimeout(tmr);
  }, []);

  const submitPayload = async (raw) => {
    const normalized = normalizeScanPayload(raw);
    if (!normalized) return;
    const qrId = extractInternalScanQrId(normalized);
    if (!qrId) {
      toast.error(t('fieldScan.unrecognizedPayload'));
      return;
    }

    const { latitude, longitude, accuracy_m } = posRef.current;
    if (latitude == null || longitude == null) {
      toast.error(t('fieldScan.needLocation'));
      refreshLocation();
      return;
    }

    setLoading(true);
    try {
      const res = await qrApi.scan({
        qr_id: qrId,
        scan_type: 'internal',
        location: 'web_hid',
        latitude,
        longitude,
        accuracy_m: accuracy_m ?? undefined,
        notes: normalized !== qrId ? `raw:${normalized.slice(0, 500)}` : undefined,
        device: {
          device_id: getWebScanDeviceId(),
          device_platform: 'web',
          device_name: 'Browser / handheld scanner (HID)',
        },
      });

      const status = res.data?.status || 'updated';
      const locationLabel = res.data?.scan_location;
      const statusUpdated = res.data?.status_updated;
      const blocked = res.data?.workflow_blocked;
      const desc = (res.data?.product_description || '').trim();
      if (blocked) {
        toast.warning(res.data?.workflow_message || t('fieldScan.workflowPending'));
      } else if (statusUpdated) {
        const locPart = locationLabel ? ` @ ${locationLabel}` : '';
        toast.success(t('fieldScan.statusUpdated', { status, location: locPart }));
      } else {
        toast.success(
          desc
            ? `${res.data?.product_name || t('fieldScan.product')}: ${desc.slice(0, 120)}${desc.length > 120 ? '…' : ''}`
            : t('fieldScan.scanOk', { status }),
        );
      }
      setLine('');
      inputRef.current?.focus();
    } catch (err) {
      const detail = err.response?.data?.detail;
      const msg =
        typeof detail === 'string' ? detail : Array.isArray(detail) ? JSON.stringify(detail) : detail?.detail || err.message || t('fieldScan.scanFailed');
      toast.error(typeof msg === 'string' ? msg : t('fieldScan.scanFailed'));
    } finally {
      setLoading(false);
    }
  };

  const onKeyDown = (e) => {
    if (e.key !== 'Enter') return;
    e.preventDefault();
    void submitPayload(line);
  };

  if (!allowed) {
    return (
      <DashboardLayout>
        <div className={`p-6 rounded-lg ${isDark ? 'bg-zinc-900 text-zinc-300' : 'bg-white text-zinc-700'}`}>
          {t('fieldScan.noPermission')}
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="max-w-2xl space-y-6" data-testid="field-scan-page">
        <div>
          <h1 className={`text-2xl font-heading font-semibold flex items-center gap-2 ${isDark ? 'text-zinc-100' : 'text-zinc-900'}`}>
            <Scan className="w-7 h-7 text-brand-500" /> {t('fieldScan.title')}
          </h1>
        </div>

        <Card className={isDark ? 'bg-zinc-900 border-zinc-800' : 'bg-white border-zinc-200'}>
          <CardContent className="pt-6 space-y-4">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div>
                <span className={`text-sm font-medium ${isDark ? 'text-zinc-200' : 'text-zinc-800'}`}>{t('fieldScan.locationStatus')}</span>
                {locReady ? (
                  <span className={`ml-2 text-sm ${isDark ? 'text-emerald-400' : 'text-emerald-700'}`}>{t('fieldScan.locationReady')}</span>
                ) : (
                  <span className={`ml-2 text-sm ${isDark ? 'text-amber-400' : 'text-amber-700'}`}>{t('fieldScan.locationWaiting')}</span>
                )}
              </div>
              <Button type="button" variant="outline" size="sm" onClick={refreshLocation} className={isDark ? 'border-zinc-600' : ''}>
                {t('fieldScan.refreshLocation')}
              </Button>
            </div>
            {locError ? <p className={`text-sm ${isDark ? 'text-red-400' : 'text-red-600'}`}>{locError}</p> : null}

            <div className="space-y-2">
              <Label htmlFor="field-scan-input" className={isDark ? 'text-zinc-300' : ''}>
                {t('fieldScan.scanFieldLabel')}
              </Label>
              <Input
                id="field-scan-input"
                ref={inputRef}
                autoComplete="off"
                autoCorrect="off"
                spellCheck={false}
                value={line}
                onChange={(e) => setLine(e.target.value)}
                onKeyDown={onKeyDown}
                disabled={loading}
                placeholder={t('fieldScan.scanPlaceholder')}
                className={isDark ? 'bg-zinc-800 border-zinc-700 text-zinc-100 font-mono text-sm' : 'font-mono text-sm'}
                data-testid="field-scan-input"
              />
            </div>

            <div className="flex gap-2">
              <Button type="button" onClick={() => submitPayload(line)} disabled={loading || !normalizeScanPayload(line)} >
                {loading ? t('fieldScan.submitting') : t('fieldScan.submitScan')}
              </Button>
              <Button type="button" variant="outline" onClick={() => setLine('')} disabled={loading} className={isDark ? 'border-zinc-600' : ''}>
                {t('fieldScan.clear')}
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
}
